import * as React from 'react';
import {createViewConfig, view} from '@sail/engine';
import type {View} from '@sail/engine';
import {useCollectionState} from '@sail/react-aria';
import type {NativeCheckboxProps} from './Checkbox';
import {Checkbox} from './Checkbox';
import useFauxTreeState from '../hooks/useFauxTreeState';
import type {FormFieldProps} from './FormField';
import {css} from '../css';

const defaultSelectedText = (itemsCount: number, totalCount: number) => {
  if (itemsCount > 0) {
    const allSelected = itemsCount === totalCount;
    if (allSelected) {
      return `Deselect all`;
    }
    return `${itemsCount} selected`;
  } else {
    return `Select all`;
  }
};

export type FauxTreeSelectAllItemProps = View.IntrinsicElement<
  'div',
  {
    selectedText?: (itemsCount: number, allCount: number) => string;
    subviews?: FormFieldProps<NativeCheckboxProps>['subviews'];
    selectAllRef?: React.MutableRefObject<HTMLInputElement | null>;
  }
>;

export const FauxTreeSelectAllItemConfig = createViewConfig({
  name: 'FauxTreeSelectAllItem',
  props: {} as FauxTreeSelectAllItemProps,
  defaults: {
    selectedText: defaultSelectedText,
  },
});

export const FauxTreeSelectAllItem = FauxTreeSelectAllItemConfig.createView(
  (allProps) => {
    const {subviews, selectedText, selectAllRef, ...props} = allProps;

    const state = useCollectionState();

    const {selectAllItem, selectGroupItems} = useFauxTreeState();

    const {
      selectedCount,
      totalCount,
      selectedHiddenCount,
      checkableKeys,
      uncheckableKeys,
      isDisabled,
    } = selectAllItem;

    const areAllSelected = totalCount === selectedCount;
    const areAllEnabledSelected =
      checkableKeys.length - selectedHiddenCount === selectedCount;

    // TODO: Sail Checkbox has a visual bug in Chrome when the values of indeterminate and checked are modified in the same render.
    // This is a workaround to avoid it, but this bug must be fixed directly in Checkbox
    const [indeterminate, setIndeterminate] = React.useState(
      selectedCount !== 0 && !areAllSelected,
    );

    React.useEffect(() => {
      setIndeterminate(selectedCount !== 0 && !areAllSelected);
    }, [selectedCount, areAllSelected]);

    const handleToggleSelectAll = React.useCallback(() => {
      if (isDisabled) {
        return;
      }
      if (areAllEnabledSelected) {
        state.selectionManager.setSelectedKeys(uncheckableKeys);
      } else {
        state.selectionManager.setSelectedKeys(checkableKeys);
      }
    }, [
      isDisabled,
      areAllEnabledSelected,
      state.selectionManager,
      checkableKeys,
      uncheckableKeys,
    ]);

    return (
      <view.div
        {...props}
        onClick={(e: React.MouseEvent) => {
          // Only handle clicks that originate from this div itself (the padding/gap area),
          // not clicks that bubble up from the checkbox or label inside.
          if (e.target === e.currentTarget) {
            handleToggleSelectAll();
          }
        }}
        css={[
          selectGroupItems.size > 0
            ? css({marginBottom: 'xsmall'})
            : css({marginBottom: 'space.0'}),
          !isDisabled ? css({cursor: 'pointer'}) : css({cursor: 'default'}),
        ]}
      >
        <Checkbox
          tabIndex={-1}
          {...(selectAllRef ? {ref: selectAllRef} : {})}
          indeterminate={indeterminate}
          label={
            selectedText
              ? selectedText(selectedCount, totalCount)
              : defaultSelectedText(selectedCount, totalCount)
          }
          checked={areAllSelected}
          disabled={isDisabled}
          subviews={{
            label: {
              css: [
                css({
                  marginLeft: '-2px',
                  font: 'label.small.emphasized',
                  color: 'subdued',
                }),
                !isDisabled
                  ? css({cursor: 'pointer'})
                  : css({cursor: 'default'}),
              ],
              // @ts-expect-error - Error after migration to React 18
              ...subviews?.label,
            },
            input: {
              css: {
                ':focus': {
                  focusRing: 'none',
                },
              },
              // @ts-expect-error - Error after migration to React 18
              ...subviews?.input,
            },
            ...subviews,
          }}
          onChange={handleToggleSelectAll}
        />
      </view.div>
    );
  },
  {
    css: {
      stack: 'x',
      font: 'label.small.emphasized',
      color: 'subdued',
      gap: 'space.75',
      borderRadius: 'xsmall',
      paddingY: 'space.75',
      paddingX: 'space.125',
      alignX: 'start',
      alignY: 'center',
      minHeight: 'xlarge',
    },
  },
);
